<template>
  <div v-if="file || digitalizationDocument" id="document-status-panel">
    <div v-if="isMetadataInspection()" class="status-container">
      <p>{{ t('generic.metadataInspection') }}</p>
      <div class="status-box">
        {{ getMetadataInspectionStatus() }}
      </div>
    </div>

    <div
      v-if="isSupplierProject() || isFormalControl()"
      class="status-container"
    >
      <p>{{ t('generic.inspection') }}</p>
      <div class="status-box">
        {{ getFormalControlStatus() }}
      </div>
    </div>

    <div v-if="isSupplierProject()" class="status-container">
      <p>{{ t('generic.inspectorStatus') }}</p>
      <div class="status-box">
        {{ getInspectorStatus() }}
      </div>
    </div>

    <div v-if="isQualityCheck()" class="status-container">
      <p>{{ t('generic.qualityCheck') }}</p>
      <div class="status-box">
        {{ getQualityCheckStatus() }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import {
    ProjectType,
    DocumentStatus,
    ISupplierDocumentDetails,
    IDigitalizationDocumentDetails,
  } from 'types';

  const { t } = useI18n();

  const props = defineProps<{
    mode: ProjectType;
    file?: ISupplierDocumentDetails;
    digitalizationDocument?: IDigitalizationDocumentDetails;
  }>();

  function isSupplierProject(): boolean {
    return props.mode === ProjectType.SUPPLIER_PROJECT;
  }

  function isMetadataInspection(): boolean {
    return (
      props.digitalizationDocument?.documentProcessingStatus ===
      DocumentStatus.METADATA_INSPECTION
    );
  }

  function getMetadataInspectionStatus(): string {
    return isSharePointUploadFailed()
      ? '-'
      : t(
          `metadataInspectionStatus.${props.digitalizationDocument?.metadataInspectionStatus}`,
        ).toString();
  }

  function isFormalControl(): boolean {
    return (
      props.digitalizationDocument?.documentProcessingStatus ===
      DocumentStatus.FORMAL_CONTROL
    );
  }

  function getFormalControlStatus(): string {
    return isSharePointUploadFailed()
      ? '-'
      : props.mode === ProjectType.SUPPLIER_PROJECT
        ? t(
            `formalControlStatus.${
              (props.file ?? props.digitalizationDocument)!.formalControlStatus
            }`,
          ).toString()
        : t(
            `digitalizationFormalControlStatus.${
              (props.file ?? props.digitalizationDocument)!.formalControlStatus
            }`,
          ).toString();
  }

  function isQualityCheck(): boolean {
    return (
      props.digitalizationDocument?.documentProcessingStatus ===
      DocumentStatus.QUALITY_CHECK
    );
  }

  function getQualityCheckStatus(): string {
    return isSharePointUploadFailed()
      ? '-'
      : t(
          `qualityCheckStatus.${props.digitalizationDocument?.qualityCheckStatus}`,
        ).toString();
  }

  function getInspectorStatus(): string {
    return isSharePointUploadFailed()
      ? '-'
      : t(`inspectorStatus.${props.file!.inspectorStatus}`).toString();
  }

  function isSharePointUploadFailed(): boolean {
    return (props.file ?? props.digitalizationDocument)!
      .didSharePointUploadFail;
  }
</script>

<style scoped lang="scss">
  #document-status-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    row-gap: 1.25rem;

    .status-container {
      flex: 1 0 calc(50% - 20px);
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      p {
        margin-bottom: 0.5rem;
      }

      .status-box {
        display: flex;
        align-items: center;
        padding: 20px;
        width: 100%;
        height: 50px;
        background-color: $form-control-disabled-color;
        border: 1px solid $border-color;
      }
    }
  }
</style>
